<script lang="ts">
  import { onMount } from 'svelte';
  import Dashboard from '../components/Dashboard.svelte';
  import { TransactionTable } from '../components/transaction';
  import FiltersInline from '../components/FiltersInline.svelte';
  import SettingsPage from '../components/SettingsPage.svelte';
  import RecurringDashboard from '../components/recurring/RecurringDashboard.svelte';
  import BudgetDashboard from '../components/budgets/BudgetDashboard.svelte';
  import Sidebar from '../components/Sidebar.svelte';
  import Visualizations from '../components/Visualizations.svelte';
  import { EmptyState } from '../components/common';
  import ConfirmDialog from '../components/common/ConfirmDialog.svelte';
  import { createConfirmDialog } from '../lib/confirmDialog.svelte';
  import { initializeStorage } from '../lib/storage';
  import { getTransactions, deleteAllTransactions, getReviewableTransactions } from '../lib/storage.js';
  import CategorizationReviewModal from '../components/categorization/CategorizationReviewModal.svelte';
  import { createRulesFromReview } from '../lib/api';
  import { calculateSummary, filterTransactions, sortTransactions } from '../lib/analytics.js';
  import type { Summary } from '../types/transaction';
  import OnboardingWelcome from '../components/onboarding/OnboardingWelcome.svelte';
  import OnboardingWizard from '../components/onboarding/OnboardingWizard.svelte';
  import OnboardingChecklist from '../components/onboarding/OnboardingChecklist.svelte';
  import { shouldShowChecklist, markChecklistItem } from '../lib/onboardingState';

  const confirmDialog = createConfirmDialog();

  // Storage initialization state
  let storageInitialized = $state(false);
  let storageError = $state<string | null>(null);
  let isInitializingStorage = $state(false);
  let showLoadingUI = $state(false); // Delayed loading UI to prevent flash

  let transactions = $state([]);
  let filteredTransactions = $state([]);
  let summary = $state<Summary | null>(null);
  // Overview tab-specific filters
  let overviewFilters = $state({
    startDate: '',
    endDate: '',
    category: [], // Array for multi-select
    person: 'all',
    institution: 'all',
    search: '',
    minAmount: '',
    maxAmount: '',
    showExcluded: false
  });

  // UI state
  let filtersExpanded = $state(false);
  let currentView = $state<'overview' | 'recurring' | 'budgets' | 'visualizations' | 'settings'>('overview');
  let sidebarCollapsed = $state(false);
  let mobileMenuOpen = $state(false);

  // Review modal state
  let reviewableTransactions = $state<{ mediumConfidence: any[], uncategorized: any[] }>({ mediumConfidence: [], uncategorized: [] });
  let showReviewModal = $state(false);

  // Onboarding state
  let showWelcome = $state(false);
  let showWizard = $state(false);
  let showChecklist = $state(shouldShowChecklist());

  // Hash-based routing
  function getViewFromHash(): 'overview' | 'recurring' | 'budgets' | 'visualizations' | 'settings' {
    const hash = window.location.hash.slice(1); // Remove the '#'
    if (hash === '/recurring') return 'recurring';
    if (hash === '/budgets') return 'budgets';
    if (hash === '/visualizations') return 'visualizations';
    if (hash === '/settings') return 'settings';
    return 'overview';
  }

  function setView(view: 'overview' | 'recurring' | 'budgets' | 'visualizations' | 'settings') {
    currentView = view;
    const routes = {
      overview: '#/',
      recurring: '#/recurring',
      budgets: '#/budgets',
      visualizations: '#/visualizations',
      settings: '#/settings'
    };
    window.location.hash = routes[view];

    // Auto-complete onboarding checklist items when user visits specific views
    if (view === 'recurring') {
      markChecklistItem('recurring');
    }
  }

  function handleHashChange() {
    currentView = getViewFromHash();
  }

  onMount(() => {
    // Set initial view from hash
    currentView = getViewFromHash();

    // Listen for hash changes (browser back/forward)
    window.addEventListener('hashchange', handleHashChange);

    // Listen for checklist dismissal
    const handleChecklistDismissed = () => {
      showChecklist = false;
    };
    window.addEventListener('onboarding-checklist-dismissed', handleChecklistDismissed);

    // Check for onboarding feature flag in URL (e.g., ?onboarding=true or ?onboarding=welcome)
    const urlParams = new URLSearchParams(window.location.search);
    const onboardingParam = urlParams.get('onboarding');

    if (onboardingParam !== null) {
      // Feature flag detected - only show onboarding when explicitly requested
      if (onboardingParam === 'welcome' || onboardingParam === 'true') {
        showWelcome = true;
        showWizard = false;
        showChecklist = false;
      } else if (onboardingParam === 'wizard') {
        showWelcome = false;
        showWizard = true;
        showChecklist = false;
      } else if (onboardingParam === 'checklist') {
        showWelcome = false;
        showWizard = false;
        showChecklist = true;
      } else {
        // Any other value (including 'false' or empty) disables onboarding
        showWelcome = false;
        showWizard = false;
        showChecklist = false;
      }
    } else {
      // No feature flag - disable onboarding by default
      showWelcome = false;
      showWizard = false;
      showChecklist = false;
    }

    // Initialize storage provider (don't await - let it run async)
    initStorage().then(() => {
      if (storageInitialized) {
        loadData();
      }
    });

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('onboarding-checklist-dismissed', handleChecklistDismissed);
    };
  });

  async function initStorage() {
    isInitializingStorage = true;
    showLoadingUI = false;
    storageError = null;

    // Only show loading UI if initialization takes longer than 200ms
    const loadingTimeout = setTimeout(() => {
      if (isInitializingStorage) {
        showLoadingUI = true;
      }
    }, 200);

    try {
      console.log('Initializing storage provider...');
      // Initialize with free tier (local SQLite storage)
      await initializeStorage({ userTier: 'free' });

      storageInitialized = true;
      console.log('Storage initialized successfully');
    } catch (error) {
      console.error('Failed to initialize storage:', error);
      storageError = error instanceof Error ? error.message : 'Unknown error';
      storageInitialized = false;
      showLoadingUI = true; // Always show UI for errors
    } finally {
      clearTimeout(loadingTimeout);
      isInitializingStorage = false;
    }
  }

  async function loadData() {
    transactions = await getTransactions();

    // Load reviewable transactions
    const reviewableResult = await getReviewableTransactions();
    reviewableTransactions = {
      mediumConfidence: reviewableResult.mediumConfidence || [],
      uncategorized: reviewableResult.uncategorized || []
    };

    applyOverviewFilters();

    // Auto-complete onboarding checklist items based on data
    if (transactions.length > 0) {
      markChecklistItem('upload');

      // If user is viewing overview, mark categories as reviewed
      if (currentView === 'overview') {
        markChecklistItem('categories');
      }
    }

    // Check for rules and budgets
    try {
      const { getStorageProvider } = await import('../lib/storage.js');
      const provider = getStorageProvider();

      const rules = await provider.getRules();
      if (rules.length > 0) {
        markChecklistItem('rule');
      }

      const budgets = await provider.getBudgets();
      if (budgets.length > 0) {
        markChecklistItem('budget');
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  }

  // Apply Overview tab filters only
  function applyOverviewFilters() {
    filteredTransactions = filterTransactions(transactions, overviewFilters);
    summary = calculateSummary(filteredTransactions);
  }

  function handleFilterChange() {
    applyOverviewFilters();
  }

  async function handleUpdate() {
    await loadData();
  }

  async function handleClearAllData() {
    const confirmed = await confirmDialog.confirm({
      title: 'Clear All Data',
      message: 'Are you sure you want to clear all transaction data? This cannot be undone.',
      confirmText: 'Clear All Data',
      variant: 'danger'
    });

    if (confirmed) {
      await deleteAllTransactions(); // This now clears processed files tracking on server
      await loadData();
    }
  }

  function handleOpenReview() {
    showReviewModal = true;
  }

  function handleReviewClose() {
    showReviewModal = false;
  }

  async function handleReviewComplete(event: CustomEvent) {
    const decisions = event.detail;

    try {
      // 1. Update all transactions based on decisions
      const transactionUpdates = [];

      for (const decision of decisions) {
        // Skip means "don't process now, show again next time"
        // So we only update confirmed transactions
        if (decision.action === 'skip') {
          continue; // Don't update skipped transactions - they'll show up again next time
        }

        // Get all transaction IDs (including grouped ones)
        const idsToUpdate = decision.groupedIds || [decision.transactionId];

        for (const id of idsToUpdate) {
          // For confirmed transactions: update category and mark as user-confirmed
          transactionUpdates.push({
            id,
            category: decision.confirmedCategory,
            confidence: 1.0,
            categorySource: decision.confirmedCategory !== decision.originalCategory ? 'user' : 'auto-confirmed'
          });
        }
      }

      // Bulk update transactions using storage provider
      if (transactionUpdates.length > 0) {
        const { getStorageProvider } = await import('../lib/storage.js');
        const provider = getStorageProvider();

        // Get all transactions to find the ones we need to update
        const allTransactions = await provider.getTransactions();
        let updatedCount = 0;

        for (const update of transactionUpdates) {
          const transaction = allTransactions.find(t => t.id === update.id);
          if (transaction) {
            // Update the transaction with new category info
            const updatedTransaction = {
              ...transaction,
              category: update.category,
              confidence: update.confidence,
              categorySource: update.categorySource
            };

            const success = await provider.upsertTransaction(updatedTransaction);
            if (success) updatedCount++;
          }
        }
      }

      // 2. Create rules for confirmed transactions where user checked "create rule"
      const ruleDecisions = decisions.filter((d: any) => d.createRule && d.action !== 'skip');

      if (ruleDecisions.length > 0) {
        const rulesToCreate = ruleDecisions.map((d: any) => ({
          description: d.description,
          category: d.confirmedCategory,
          person: null
        }));

        await createRulesFromReview(rulesToCreate);
      }

      showReviewModal = false;

      // Reload data to update the list of reviewable transactions
      await loadData();
    } catch (error) {
      console.error('Error in review complete:', error);
    }
  }

  // Onboarding handlers
  function handleStartWizard() {
    showWelcome = false;
    showWizard = true;
  }

  function handleSkipTour() {
    showWelcome = false;
    showWizard = false;
  }

  function handleWizardComplete() {
    showWizard = false;
  }
</script>

<div class="app">
  <!-- Mobile menu toggle button (edge-mounted) -->
  <button class="mobile-toggle-btn" class:open={mobileMenuOpen} onclick={() => mobileMenuOpen = !mobileMenuOpen} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}>
    {#if mobileMenuOpen}
      <!-- Small arrow when open -->
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <path d="M13 4L7 10L13 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    {:else}
      <!-- Plutus icon when closed -->
      <img src="/plutus-icon.png" alt="Menu" class="toggle-icon" />
    {/if}
  </button>

  <!-- Sidebar -->
  <Sidebar
    {currentView}
    onViewChange={setView}
    bind:isCollapsed={sidebarCollapsed}
    onToggleCollapse={() => sidebarCollapsed = !sidebarCollapsed}
    bind:isMobileMenuOpen={mobileMenuOpen}
    onMobileMenuClose={() => mobileMenuOpen = false}
  />

  <div class="main-content" class:sidebar-collapsed={sidebarCollapsed}>
    {#if showLoadingUI && isInitializingStorage}
      <!-- Loading State -->
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p>Initializing database...</p>
      </div>
    {:else if storageError}
      <!-- Error State -->
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <h2 class="error-title">Failed to Initialize Database</h2>
        <p class="error-message">{storageError}</p>
        <button class="retry-button" onclick={initStorage}>
          Retry
        </button>
        <p class="error-help">
          If this problem persists, try clearing your browser data or using a different browser.
        </p>
      </div>
    {:else if storageInitialized}
      <!-- App Content -->
      <div class="container">
        {#if transactions.length > 0}
        {#if currentView === 'overview'}
          <!-- Onboarding Checklist -->
          {#if showChecklist}
            <OnboardingChecklist />
          {/if}

          <Dashboard {summary} />

          <!-- Review Alert Banner -->
          {#if reviewableTransactions.mediumConfidence.length > 0 || reviewableTransactions.uncategorized.length > 0}
            {@const totalCount = reviewableTransactions.mediumConfidence.length + reviewableTransactions.uncategorized.length}
            <div class="review-alert-banner">
              <div class="alert-icon">⚠️</div>
              <div class="alert-content">
                <h3>{totalCount} Transaction Group{totalCount === 1 ? '' : 's'} Need Review</h3>
                <p>
                  {#if reviewableTransactions.mediumConfidence.length > 0 && reviewableTransactions.uncategorized.length > 0}
                    {reviewableTransactions.mediumConfidence.length} with name matched suggestions and {reviewableTransactions.uncategorized.length} frequent uncategorized transaction{reviewableTransactions.uncategorized.length === 1 ? '' : 's'}. Review to confirm categories and create automatic rules.
                  {:else if reviewableTransactions.mediumConfidence.length > 0}
                    These transactions have AI-suggested categories with 75-85% confidence. Review to confirm and create automatic rules.
                  {:else}
                    These uncategorized transactions appear 3+ times. Categorize them to automatically apply to all similar transactions.
                  {/if}
                </p>
              </div>
              <button class="review-button" onclick={handleOpenReview}>
                Review Now
              </button>
            </div>
          {/if}

          <section class="transactions-section">
            <div class="section-header">
              <h2>Transactions ({filteredTransactions.length}/{transactions.length})</h2>
              <button class="filters-toggle-btn" onclick={() => filtersExpanded = !filtersExpanded}>
                <span>Filters {filtersExpanded ? '▲' : '▼'}</span>
              </button>
            </div>

            <FiltersInline
              filters={overviewFilters}
              onFilterChange={handleFilterChange}
              bind:isExpanded={filtersExpanded}
              autoInitializeCategories={false}
            />

            <TransactionTable transactions={filteredTransactions} showExcluded={overviewFilters.showExcluded} onUpdate={handleUpdate} />
          </section>
        {:else if currentView === 'recurring'}
          <RecurringDashboard />
        {:else if currentView === 'budgets'}
          <BudgetDashboard />
        {:else if currentView === 'visualizations'}
          <Visualizations allTransactions={transactions} />
        {:else if currentView === 'settings'}
          <SettingsPage onUpdate={handleUpdate} onClearAllData={handleClearAllData} />
        {/if}
      {:else}
        {#if currentView === 'settings'}
          <SettingsPage onUpdate={handleUpdate} onClearAllData={handleClearAllData} />
        {:else}
          <EmptyState
            title="No transactions yet"
            message="Go to Settings → Data Management to upload CSV files from your bank accounts"
          />
        {/if}
      {/if}
      </div>
    {/if}
  </div>
</div>

<ConfirmDialog {...confirmDialog.props} />

<!-- Categorization Review Modal - only render after storage is initialized -->
{#if storageInitialized}
  <CategorizationReviewModal
    isOpen={showReviewModal}
    mediumConfidenceTransactions={reviewableTransactions.mediumConfidence}
    uncategorizedTransactions={reviewableTransactions.uncategorized}
    allTransactions={transactions}
    onClose={handleReviewClose}
    onComplete={(decisions) => handleReviewComplete(new CustomEvent('complete', { detail: decisions }))}
  />
{/if}

<!-- Onboarding Modals -->
{#if showWelcome}
  <OnboardingWelcome onStartWizard={handleStartWizard} onSkipTour={handleSkipTour} />
{/if}

{#if showWizard}
  <OnboardingWizard onComplete={handleWizardComplete} />
{/if}

<style lang="scss">
  .app {
    min-height: 100vh;
    overflow: visible;
    display: flex;
  }

  .mobile-toggle-btn {
    display: none;
    position: fixed;
    top: var(--spacing-2xl);
    left: 0; /* Sits at left edge when drawer is closed */
    z-index: calc(var(--z-modal) + 2); /* Above sidebar and backdrop */
    background: var(--gradient-header);
    border: none;
    border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
    width: 48px; /* Big button when closed */
    height: 48px;
    padding: var(--spacing-xs);
    cursor: pointer;
    color: white;
    transition: left var(--transition-base), width var(--transition-base), height var(--transition-base), padding var(--transition-base), color var(--transition-fast), box-shadow var(--transition-fast);
    box-shadow: var(--shadow-lg);
    align-items: center;
    justify-content: center;

    svg {
      width: var(--spacing-md);
      height: var(--spacing-md);
      flex-shrink: 0;
    }

    .toggle-icon {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }

    &:hover {
      color: white;
      box-shadow: var(--shadow-xl);
    }

    &:active {
      transform: scale(0.98);
    }

    /* When drawer is open - small button at right edge */
    &.open {
      left: var(--sidebar-width-collapsed);
      width: var(--spacing-sm); /* Small like desktop collapse */
      height: var(--spacing-3xl);
      padding: 0;
      border-radius: 0 var(--radius-sm) var(--radius-sm) 0;

      svg {
        margin-left: -1px;
      }
    }
  }

  .main-content {
    flex: 1;
    margin-left: var(--sidebar-width);
    min-height: 100vh;
    transition: margin-left var(--transition-base);

    &.sidebar-collapsed {
      margin-left: var(--sidebar-width-collapsed);
    }
  }

  .container {
    max-width: calc(100vw - var(--sidebar-width));
    margin: 0 auto;
    padding: var(--spacing-2xl) var(--spacing-2xl);
    overflow: visible;
  }


  /* Loading State */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: var(--spacing-xl);
    padding: var(--spacing-4xl);

    p {
      font-size: var(--font-size-lg);
      color: var(--color-text-secondary);
      margin: 0;
    }
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: var(--radius-full);
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Error State */
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: var(--spacing-lg);
    padding: var(--spacing-4xl);
    text-align: center;
    max-width: 500px;
    margin: 0 auto;
  }

  .error-icon {
    font-size: var(--font-size-5xl);
    line-height: 1;
  }

  .error-title {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0;
  }

  .error-message {
    font-size: var(--font-size-base);
    color: var(--color-text-secondary);
    margin: 0;
    padding: var(--spacing-md);
    background: var(--color-background-secondary);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    font-family: var(--font-family-mono);
    word-break: break-word;
  }

  .retry-button {
    padding: var(--spacing-md) var(--spacing-2xl);
    background: var(--color-primary);
    color: white;
    border: none;
    border-radius: var(--radius-md);
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    cursor: pointer;
    transition: all var(--transition-fast);
    box-shadow: var(--shadow-sm);

    &:hover {
      background: var(--color-primary-hover);
      box-shadow: var(--shadow-md);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .error-help {
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
    margin: 0;
    font-style: italic;
  }

  .main-content.sidebar-collapsed .container {
    max-width: calc(100vw - var(--sidebar-width-collapsed));
  }

  .transactions-section {
    margin-top: var(--spacing-3xl);
    overflow: visible;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-lg);
    flex-wrap: wrap;
    gap: var(--spacing-md);

    h2 {
      margin: 0;
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
    }

    .filters-toggle-btn {
      padding: var(--spacing-sm) var(--spacing-lg);
      background: var(--color-background);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      cursor: pointer;
      transition: all var(--transition-fast);

      &:hover {
        background: var(--color-background-secondary);
        border-color: var(--color-text-muted);
      }
    }
  }

  @media (max-width: 1024px) {
    .mobile-toggle-btn {
      display: flex;
      overflow: hidden;
    }
   

    .main-content {
      margin-left: 0;

      &.sidebar-collapsed {
        margin-left: 0;
      }
    }

    .container {
      padding: var(--spacing-lg);
      max-width: calc(100vw - var(--sidebar-width-collapsed));
    }
  }

  @media (max-width: 768px) {
    .container {
        max-width: 98vw;
      padding: var(--spacing-sm);
    }

    .section-header {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-sm);

      h2 {
        font-size: var(--font-size-2xl);
      }
    }

    .transactions-section {
      margin-top: var(--spacing-2xl);
    }


  }

  @media (max-width: 640px) {
    .container {
      padding: var(--spacing-xs);
    max-width: 100vw;

    }

    .section-header {
      h2 {
        font-size: var(--font-size-xl);
      }

      .filters-toggle-btn {
        width: 100%;
        justify-content: center;
      }
    }

    .transactions-section {
      margin-top: var(--spacing-xl);
    }
  }

  .review-alert-banner {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg);
    padding: var(--spacing-lg);
    margin: var(--spacing-lg) 0;
    background: var(--color-warning-bg, #fff3cd);
    border: 2px solid var(--color-warning, #ffc107);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);

    .alert-icon {
      font-size: var(--font-size-3xl);
      flex-shrink: 0;
    }

    .alert-content {
      flex: 1;

      h3 {
        margin: 0 0 var(--spacing-xs) 0;
        font-size: var(--font-size-lg);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-primary);
      }

      p {
        margin: 0;
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
        line-height: 1.5;
      }
    }

    .review-button {
      padding: var(--spacing-sm) var(--spacing-xl);
      background: var(--color-warning, #ffc107);
      color: var(--color-text-primary);
      border: none;
      border-radius: var(--radius-md);
      font-size: var(--font-size-base);
      font-weight: var(--font-weight-semibold);
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;

      &:hover {
        background: color-mix(in srgb, var(--color-warning, #ffc107) 85%, black);
        transform: translateY(-1px);
        box-shadow: var(--shadow-md);
      }

      &:active {
        transform: translateY(0);
      }
    }

    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;

      .review-button {
        width: 100%;
      }
    }
  }
</style>
